import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { UX2 } from '@wsb/guac-widget-core';

import { ErrorMessage } from '../../shop/components/lazy';
import ProductListItem from './ProductListItem';
import YotpoCSS from '../../shop/components/products/YotpoCSS';
import Pagination from '../../shop/components/Pagination';
import dataAids from '../../constants/dataAids';
import * as MetricEvents from '../../constants/metrics';
import useDevice, { devices } from '../../shop/hooks/useDevice';

// this object maps the product list metrics to the corresponding metric events
const productMetrics = {
  click: MetricEvents.PRODUCT_LIST_PRODUCT_CLICK,
  limitedEditionEyebrowImpression:
    MetricEvents.PRODUCT_LIST_LIMITED_EDITION_EYEBROW_PRODUCT_IMPRESSION,
  limitedEditionEyebrowClick: MetricEvents.PRODUCT_LIST_LIMITED_EDITION_EYEBROW_PRODUCT_CLICK,
  freeShippingEyebrowImpression: MetricEvents.PRODUCT_LIST_FREE_SHIPPING_EYEBROW_PRODUCT_IMPRESSION,
  freeShippingEyebrowClick: MetricEvents.PRODUCT_LIST_FREE_SHIPPING_EYEBROW_PRODUCT_CLICK,
  freeShippingAndReturnsEyebrowImpression:
    MetricEvents.PRODUCT_LIST_FREE_SHIPPING_AND_RETURNS_EYEBROW_PRODUCT_IMPRESSION,
  freeShippingAndReturnsEyebrowClick:
    MetricEvents.PRODUCT_LIST_FREE_SHIPPING_AND_RETURNS_EYEBROW_PRODUCT_CLICK,
  freeReturnsEyebrowImpression: MetricEvents.PRODUCT_LIST_FREE_RETURNS_EYEBROW_PRODUCT_IMPRESSION,
  freeReturnsEyebrowClick: MetricEvents.PRODUCT_LIST_FREE_RETURNS_EYEBROW_PRODUCT_CLICK,
  quickViewButtonClick: MetricEvents.PRODUCT_LIST_QUICK_VIEW_BUTTON_CLICK
};

function ProductListData({
  error,
  products,
  layoutDesktopColumns,
  layoutMobileColumns,
  showCategories,
  translate,
  imageCropMethod,
  galleryShowBorder,
  galleryImageShape,
  smartRibbonsSale,
  smartRibbonsBestseller,
  smartRibbonsNewProduct,
  smartRibbonsSaleOption,
  smartRibbonsBestsellerOption,
  smartRibbonsNewProductOption,
  smartRibbonStyle,
  isSearchView,
  renderMode,
  showProductNotFound,
  onPaginationPageChange,
  pages,
  currentPage,
  viewDevice,
  section,
  isOlsQuickViewOn,
  pdpImageCropMethod,
  pdpImageShape,
  useBruce,
  productsUrl
}) {
  const {
    Element: { Block, Text },
    Component: {
      Grid,
      Grid: { Cell },
      PlaceholderMessage
    }
  } = UX2;
  const [horizontalPadding, setHorizontalPadding] = useState(null);

  const mobileStyleProps = () => {
    if (+layoutMobileColumns === 1) {
      const container = document.querySelector('[data-container-id="ols-shop"]');
      if (container) {
        const containerStyles = window.getComputedStyle(container);
        return containerStyles.paddingLeft;
      }
    }

    return {};
  };

  useEffect(() => {
    setHorizontalPadding(mobileStyleProps);
  }, []);

  const [device] = useDevice();
  const isMobile = device === devices.MOBILE || viewDevice === 'MOBILE_RENDER_DEVICE';

  const styles = {
    grid: {
      justifyContent:
        (products.length === 1 || products.length === 2) && !showCategories ? 'center' : 'start'
    },
    pagination: {
      'display': 'flex',
      'marginTop': 'small',
      'justifyContent': 'center',
      '@sm': {
        justifyContent: 'flex-end'
      }
    }
  };

  let productData;
  if (error && error.hasOwnProperty('status')) {
    productData = <ErrorMessage message={ translate('products.error_fetching_product_data') } />;
  } else if (products.length) {
    const productsPerPage = 5 * +layoutDesktopColumns;
    productData = (
      <Grid
        data-aid={ dataAids.PRODUCT_LIST_RENDERED }
        data-products-per-page={ productsPerPage }
        xs={ +layoutMobileColumns }
        spacingXs='xsmall'
        md={ +layoutDesktopColumns }
        style={ styles.grid }
      >
        { products.map(product => (
          <Cell key={ product.slug } style={{ marginBottom: 'large' }}>
            <ProductListItem
              product={ product }
              renderMode={ renderMode }
              cardSize={{
                lg: +layoutDesktopColumns > 3 && showCategories ? 'small' : 'large',
                md:
                  +layoutDesktopColumns > 3 || (+layoutDesktopColumns > 2 && showCategories)
                    ? 'small'
                    : 'large',
                xs: +layoutMobileColumns > 1 ? 'small' : 'large'
              }}
              metrics={ productMetrics }
              translate={ translate }
              imageCropMethod={ imageCropMethod }
              galleryShowBorder={ galleryShowBorder }
              galleryImageShape={ galleryImageShape }
              smartRibbonsSale={ smartRibbonsSale }
              smartRibbonsBestseller={ smartRibbonsBestseller }
              smartRibbonsNewProduct={ smartRibbonsNewProduct }
              selectedSaleRibbon={ smartRibbonsSaleOption }
              selectedBestsellerRibbon={ smartRibbonsBestsellerOption }
              selectedNewProductRibbon={ smartRibbonsNewProductOption }
              smartRibbonStyle={ smartRibbonStyle }
              canUseQuickView={ isOlsQuickViewOn && !isMobile }
              canUseCarousel={ isMobile && +layoutMobileColumns === 1 }
              carouselWidthOffset={ horizontalPadding && `-${horizontalPadding}` }
              pdpImageCropMethod={ pdpImageCropMethod }
              pdpImageShape={ pdpImageShape }
              useBruce={ useBruce }
              productsUrl={ productsUrl }
            />
          </Cell>
        )) }
      </Grid>
    );
  } else if (!isSearchView) {
    const textProps = {
      'data-aid': dataAids.NO_PRODUCTS_MESSAGE_RENDERED
    };
    productData = (
      <PlaceholderMessage
        section={ section === 'default' ? 'alt' : 'default' }
        message={ translate('products.no_products_message') }
        textProps={ textProps }
      />
    );
  }

  return (
    <Block>
      <YotpoCSS products={ products } />
      { showProductNotFound && renderMode === 'PREVIEW' && (
        <Block
          section='alt'
          data-aid={ dataAids.PRODUCT_NOT_FOUND_MESSAGE_RENDERED }
          style={ styles.productNotFound }
        >
          <Text
            style={ styles.productNotFoundText }
            children={ translate('products.product_no_longer_available_2') }
          />
        </Block>
      ) }
      { productData }
      { pages > 1 && (
        <Block style={ styles.pagination }>
          <Pagination
            currentPage={ currentPage }
            pages={ pages }
            onPageChange={ onPaginationPageChange }
          />
        </Block>
      ) }
    </Block>
  );
}

ProductListData.propTypes = {
  error: PropTypes.object,
  products: PropTypes.array,
  layoutDesktopColumns: PropTypes.number,
  layoutMobileColumns: PropTypes.number,
  showCategories: PropTypes.bool,
  translate: PropTypes.func,
  imageCropMethod: PropTypes.string,
  galleryShowBorder: PropTypes.bool,
  galleryImageShape: PropTypes.string,
  smartRibbonsSale: PropTypes.bool,
  smartRibbonsBestseller: PropTypes.bool,
  smartRibbonsNewProduct: PropTypes.bool,
  smartRibbonsSaleOption: PropTypes.string,
  smartRibbonsBestsellerOption: PropTypes.string,
  smartRibbonsNewProductOption: PropTypes.string,
  smartRibbonStyle: PropTypes.string,
  isSearchView: PropTypes.bool,
  renderMode: PropTypes.string,
  showProductNotFound: PropTypes.bool,
  onPaginationPageChange: PropTypes.func,
  pages: PropTypes.any,
  currentPage: PropTypes.any,
  viewDevice: PropTypes.string,
  section: PropTypes.string,
  isOlsQuickViewOn: PropTypes.bool,
  pdpImageShape: PropTypes.string,
  pdpImageCropMethod: PropTypes.string,
  useBruce: PropTypes.bool,
  productsUrl: PropTypes.string
};

export default ProductListData;
